import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { getDocumentMasterList, DocumentMasterListItem, DocumentMasterListResponse } from '../../api/project-documents';
import { getDisciplines } from '../../api/disciplines';
import { getAimsDocumentTypes } from '../../api/aims-document-types';
import { ORDER } from '../../common/enum/order';

export interface AimsDocumentType {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface Discipline {
  id: number;
  name: string;
  cfihosName: string;
  createdAt: string;
  updatedAt: string;
}

// Document Master List query parameters
export interface DocumentMasterListParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  orderBy?: ORDER;
  search?: string;
  disciplineIds?: number[];
  aimsDocumentTypeIds?: number[];
}

export interface DocumentMasterListState {
  documents: {
    data: DocumentMasterListItem[];
    count: number;
    loading: boolean;
    error: string | null;
  };
  disciplines: {
    data: Discipline[];
    loading: boolean;
    error: string | null;
  };
  aimsDocumentTypes: {
    data: AimsDocumentType[];
    loading: boolean;
    error: string | null;
  };
}

const initialState: DocumentMasterListState = {
  documents: {
    data: [],
    count: 0,
    loading: false,
    error: null
  },
  disciplines: {
    data: [],
    loading: false,
    error: null
  },
  aimsDocumentTypes: {
    data: [],
    loading: false,
    error: null
  }
};

// Async actions
export const getDocumentMasterListAsync = createAsyncThunk<
  DocumentMasterListResponse,
  DocumentMasterListParams
>(
  'documentMasterList/getDocuments',
  async (params: DocumentMasterListParams, { rejectWithValue }) => {
    try {
      const response = await getDocumentMasterList(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to fetch documents');
    }
  }
);

export const getDisciplinesAsync = createAsyncThunk(
  'documentMasterList/getDisciplines',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDisciplines();
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to fetch disciplines');
    }
  }
);

export const getAimsDocumentTypesAsync = createAsyncThunk(
  'documentMasterList/getAimsDocumentTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAimsDocumentTypes();
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to fetch AIMS document types');
    }
  }
);

const documentMasterListSlice = createSlice({
  name: 'documentMasterList',
  initialState,
  reducers: {
    clearDocuments: (state) => {
      state.documents.data = [];
      state.documents.count = 0;
      state.documents.error = null;
    },
    clearDisciplines: (state) => {
      state.disciplines.data = [];
      state.disciplines.error = null;
    },
    clearAimsDocumentTypes: (state) => {
      state.aimsDocumentTypes.data = [];
      state.aimsDocumentTypes.error = null;
    },
    clearErrors: (state) => {
      state.documents.error = null;
      state.disciplines.error = null;
      state.aimsDocumentTypes.error = null;
    }
  },
  extraReducers: (builder) => {
    // Documents
    builder.addCase(getDocumentMasterListAsync.pending, (state) => {
      state.documents.loading = true;
      state.documents.error = null;
    });
    builder.addCase(getDocumentMasterListAsync.fulfilled, (state, action) => {
      state.documents.loading = false;
      state.documents.data = action.payload.data;
      state.documents.count = action.payload.count;
      state.documents.error = null;
    });
    builder.addCase(getDocumentMasterListAsync.rejected, (state, action) => {
      state.documents.loading = false;
      state.documents.error = action.payload as string;
    });

    // Disciplines
    builder.addCase(getDisciplinesAsync.pending, (state) => {
      state.disciplines.loading = true;
      state.disciplines.error = null;
    });
    builder.addCase(getDisciplinesAsync.fulfilled, (state, action: PayloadAction<Discipline[]>) => {
      state.disciplines.loading = false;
      state.disciplines.data = action.payload;
      state.disciplines.error = null;
    });
    builder.addCase(getDisciplinesAsync.rejected, (state, action) => {
      state.disciplines.loading = false;
      state.disciplines.error = action.payload as string;
    });

    // AIMS Document Types
    builder.addCase(getAimsDocumentTypesAsync.pending, (state) => {
      state.aimsDocumentTypes.loading = true;
      state.aimsDocumentTypes.error = null;
    });
    builder.addCase(getAimsDocumentTypesAsync.fulfilled, (state, action: PayloadAction<AimsDocumentType[]>) => {
      state.aimsDocumentTypes.loading = false;
      state.aimsDocumentTypes.data = action.payload;
      state.aimsDocumentTypes.error = null;
    });
    builder.addCase(getAimsDocumentTypesAsync.rejected, (state, action) => {
      state.aimsDocumentTypes.loading = false;
      state.aimsDocumentTypes.error = action.payload as string;
    });
  }
});

export const { clearDocuments, clearDisciplines, clearAimsDocumentTypes, clearErrors } = documentMasterListSlice.actions;

// Selectors
export const selectDocumentMasterList = (state: RootState) => state.documentMasterList as DocumentMasterListState;
export const selectDocuments = (state: RootState) => (state.documentMasterList as DocumentMasterListState).documents;
export const selectDisciplines = (state: RootState) => (state.documentMasterList as DocumentMasterListState).disciplines;
export const selectAimsDocumentTypes = (state: RootState) => (state.documentMasterList as DocumentMasterListState).aimsDocumentTypes;

export default documentMasterListSlice.reducer;